import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { FlaskConical, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

const eur = (v) => `${Math.round(v || 0).toLocaleString('fr-FR')} €`;
const inputCls = "h-9 w-full rounded-lg border border-[#E9DDCC] bg-white px-2.5 text-sm text-[#493329] focus:outline-none focus:ring-2 focus:ring-[#C99A55]";
const lblCls = "text-xs text-[#7A6455] font-medium";

export default function CapacitySimPanel() {
  const [data, setData] = useState(null);
  const [sim, setSim] = useState({ reformers: '', monthly_hours: '', objective: '' });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/stats/full`, { withCredentials: true });
      setData(res.data);
      resetToReal(res.data);
    } catch (e) {
      toast.error('Impossible de charger les données de capacité.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetToReal = (d) => {
    setSim({
      reformers: d.settings.reformers,
      monthly_hours: d.settings.monthly_hours,
      objective: d.goal.target,
    });
  };

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <Card className="border-[#E9DDCC] rounded-2xl">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[#C99A55]" />
        </CardContent>
      </Card>
    );
  }

  const sc = data.scopes.confirmed;
  const cwp = sc.with_plan || 0;
  const avgSessions = cwp ? sc.sessions_engagees / cwp : 0;
  const avgCA = cwp ? sc.ca_mensuel_total / cwp : 0;
  const avgRec = cwp ? sc.ca_recurrent / cwp : 0;

  const reformers = Math.max(0, parseInt(sim.reformers, 10) || 0);
  const hours = Math.max(0, parseInt(sim.monthly_hours, 10) || 0);
  const objective = Math.max(0, parseInt(sim.objective, 10) || 0);

  const capacity = hours * reformers;
  const sessionsEst = Math.round(avgSessions * objective);
  const caEst = avgCA * objective;
  const caRecEst = avgRec * objective;
  const fill = capacity ? Math.round((sessionsEst / capacity) * 1000) / 10 : 0;
  const remaining = capacity - sessionsEst;
  const thr = data.capacity.thresholds || { warning: 75, critical: 85 };
  const satLevel = fill >= thr.critical ? 'red' : fill >= thr.warning ? 'orange' : 'green';
  const satLabel = satLevel === 'red' ? 'Risque de saturation' : satLevel === 'orange' ? 'Capacité à surveiller' : 'Capacité confortable';

  const isReal = reformers === data.settings.reformers && hours === data.settings.monthly_hours && objective === data.goal.target;

  return (
    <Card className="border border-[#E9DDCC] rounded-2xl bg-white" data-testid="capacity-sim-panel">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <FlaskConical className="w-4 h-4 text-[#C99A55]" strokeWidth={1.8} />
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#C99A55] font-semibold">Simulateur</span>
            </div>
            <h3 className="font-heading text-lg text-[#493329]">Simulation libre de capacité & revenus</h3>
          </div>
          <button onClick={() => resetToReal(data)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs border border-[#E9DDCC] text-[#7A6455] hover:border-[#C99A55]"
            data-testid="sim-reset-btn">
            <RotateCcw className="w-3.5 h-3.5" /> Revenir aux paramètres réels
          </button>
        </div>

        <p className="text-[11px] text-[#AE9885] italic">
          Ce simulateur n'affecte <strong>aucun</strong> paramètre réel. Pour modifier les vraies valeurs, utilisez « Paramètres du studio » ci-dessus.
          Il s'appuie sur le mix actuel des formules (panier moyen {eur(avgCA)} TTC et {avgSessions.toFixed(1)} séances/cliente).
        </p>

        {/* Saisies */}
        <div className="grid grid-cols-3 gap-3">
          <label className="block"><span className={lblCls}>Nombre de Reformer</span>
            <input type="number" min="0" max="50" value={sim.reformers}
              onChange={(e) => setSim({ ...sim, reformers: e.target.value })} className={`${inputCls} mt-1`} data-testid="sim-reformers" /></label>
          <label className="block"><span className={lblCls}>Heures de salle / mois</span>
            <input type="number" min="0" max="744" value={sim.monthly_hours}
              onChange={(e) => setSim({ ...sim, monthly_hours: e.target.value })} className={`${inputCls} mt-1`} data-testid="sim-hours" /></label>
          <label className="block"><span className={lblCls}>Objectif clientes</span>
            <input type="number" min="0" max="1000" value={sim.objective}
              onChange={(e) => setSim({ ...sim, objective: e.target.value })} className={`${inputCls} mt-1`} data-testid="sim-objective" /></label>
        </div>

        {!isReal && (
          <div className="text-[11px] text-[#9A744C] bg-[#FFF8E6] border border-[#C99A55]/40 rounded-lg px-3 py-1.5" data-testid="sim-hypo-badge">
            Hypothèse de simulation — différente des paramètres réels ({data.settings.monthly_hours} h × {data.settings.reformers} Reformer, objectif {data.goal.target}).
          </div>
        )}

        {/* Résultats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center text-sm" data-testid="sim-results">
          <div className="bg-[#F5EFE5] rounded-lg p-2.5"><div className="text-[10px] uppercase text-[#AE9885]">Capacité / mois</div><div className="font-heading text-lg text-[#493329]" data-testid="sim-capacity">{capacity}</div></div>
          <div className="bg-[#F5EFE5] rounded-lg p-2.5"><div className="text-[10px] uppercase text-[#AE9885]">Séances estimées</div><div className="font-heading text-lg text-[#493329]" data-testid="sim-sessions">{sessionsEst}</div></div>
          <div className="bg-[#F5EFE5] rounded-lg p-2.5"><div className="text-[10px] uppercase text-[#AE9885]">Places restantes</div><div className={`font-heading text-lg ${remaining < 0 ? 'text-[#C76542]' : 'text-[#493329]'}`} data-testid="sim-remaining">{remaining}</div></div>
          <div className="bg-[#F5EFE5] rounded-lg p-2.5 border border-[#C99A55]/40"><div className="text-[10px] uppercase text-[#C99A55]">Remplissage</div><div className="font-heading text-lg text-[#C99A55]" data-testid="sim-fill">{fill} %</div></div>
          <div className="bg-[#F5EFE5] rounded-lg p-2.5"><div className="text-[10px] uppercase text-[#AE9885]">CA mensuel estimé TTC</div><div className="font-heading text-lg text-[#493329]" data-testid="sim-ca">{eur(caEst)}</div></div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-2 text-sm">
          <span className="text-[#7A6455]">dont CA récurrent estimé : <strong>{eur(caRecEst)} TTC</strong></span>
          <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium ${
            satLevel === 'red' ? 'bg-red-100 text-red-700'
              : satLevel === 'orange' ? 'bg-amber-100 text-amber-700'
              : 'bg-emerald-100 text-emerald-700'}`} data-testid="sim-saturation">
            {satLabel}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
